// Packaging.State — registry + invocation table + catalogue.
//
// Per `.docs/v1/design/bounded-contexts.md` §3. Three collections at
// MVP:
//
//   - `packagings` — the registry. Seeded with six built-in entries
//     at `initial` per Slice 5. No registry-mutation commands ship at
//     MVP (Q from §3 shortcuts).
//   - `invocations` — running / completed / failed lifecycle.
//   - `catalogueEntries` — silent agent-attempt log.
//
// The checksum register lives on `Resource.State`, not here — see
// `bounded-contexts.md` §1 for the ownership rationale.
export { builtInRegistry, findByEndpoints, initial, mkPackagingState, outputTemplateFor, packageableFormats, };
import { exhaustSwitchCase, Maybe } from "@bilda/utils";
import * as FormatId from "../format/id.js";
import * as FormatMod from "../format.js";
import * as Template from "../template.js";
import * as Version from "../versioning/version.js";
import * as Endpoint from "./endpoint.js";
import * as Fidelity from "./fidelity.js";
import * as IdMod from "./id.js";
import * as PackagingMod from "./packaging.js";
import * as RefMod from "./ref.js";
const mkPackagingState = (packagings, invocations, catalogueEntries) => ({ packagings, invocations, catalogueEntries });
// === Built-in registry (Slice 5) ===
//
// The built-in Packagings (one per supported template→format pair).
// Every one uses the standard trust + deterministic flavour + empty
// fidelity contract — richer values arrive when the first adapter
// ships and needs them.
//
// Ids preserve a design-side prefix (`P_*`) so bundle serialisations
// line up.
const builtInTrust = PackagingMod.mkTrust("built_in");
const builtInFlavour = PackagingMod.mkFlavour("deterministic");
const mkBuiltInRef = (idString) => RefMod.mk(IdMod.mk(idString), Version.v1);
// F_pdf → T_pdf_import (F→T)
const pdfImport = PackagingMod.mkPackaging(IdMod.mk("P_pdf_import"), mkBuiltInRef("P_pdf_import"), Endpoint.mkFormatEndpoint(FormatMod.pdf), Endpoint.mkTemplateEndpoint(Template.pdfImport.ref), Fidelity.emptyFidelity, Maybe.just(Template.pdfImport.ref), builtInTrust, builtInFlavour);
// T_freeform → F_text (T→F, no mirror — text passthrough has no
// structural shape to name)
const freeformText = PackagingMod.mkPackaging(IdMod.mk("P_freeform_text"), mkBuiltInRef("P_freeform_text"), Endpoint.mkTemplateEndpoint(Template.freeform.ref), Endpoint.mkFormatEndpoint(FormatMod.text), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_freeform → F_torus (T→F, no mirror — the freeform text is wrapped
// verbatim into a minimal Torus digest at render time; no structural
// mirror Template needed for MVP. Post-MVP: `T_quiz → F_torus` with a
// `torusQuiz` mirror once quiz Content has structural elements to map
// to Torus activities.)
const freeformTorus = PackagingMod.mkPackaging(IdMod.mk("P_freeform_torus"), mkBuiltInRef("P_freeform_torus"), Endpoint.mkTemplateEndpoint(Template.freeform.ref), Endpoint.mkFormatEndpoint(FormatMod.torus), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_freeform → F_html (T→F, no mirror — the freeform content is
// wrapped verbatim into a self-contained HTML document at render
// time, the keystone physical output). Every material type can reach
// a portable HTML deliverable; freeform is the "just write" case.
const freeformHtml = PackagingMod.mkPackaging(IdMod.mk("P_freeform_html"), mkBuiltInRef("P_freeform_html"), Endpoint.mkTemplateEndpoint(Template.freeform.ref), Endpoint.mkFormatEndpoint(FormatMod.html), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_pqbl_torus → F_html (T→F, no mirror — the pqbl-torus Content
// renders directly to a structured HTML quiz handout via the shell's
// htmlRenderer; correct choices are marked and feedback is shown, so
// the output doubles as an answer key). Physical/portable counterpart
// to the F_torus / F_canvas LMS targets.
const pqblTorusHtml = PackagingMod.mkPackaging(IdMod.mk("P_pqbl_torus_html"), mkBuiltInRef("P_pqbl_torus_html"), Endpoint.mkTemplateEndpoint(Template.pqblTorus.ref), Endpoint.mkFormatEndpoint(FormatMod.html), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_freeform → F_pdf (T→F, no mirror — the freeform content is laid
// out as a paginated print-ready PDF handout at render time). The
// physical print floor; rides the same content as F_html.
const freeformPdf = PackagingMod.mkPackaging(IdMod.mk("P_freeform_pdf"), mkBuiltInRef("P_freeform_pdf"), Endpoint.mkTemplateEndpoint(Template.freeform.ref), Endpoint.mkFormatEndpoint(FormatMod.pdf), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_pqbl_torus → F_pdf (T→F, no mirror — the pqbl-torus Content
// renders to a paginated quiz handout PDF with correct choices marked;
// print/worksheet counterpart to the F_html output).
const pqblTorusPdf = PackagingMod.mkPackaging(IdMod.mk("P_pqbl_torus_pdf"), mkBuiltInRef("P_pqbl_torus_pdf"), Endpoint.mkTemplateEndpoint(Template.pqblTorus.ref), Endpoint.mkFormatEndpoint(FormatMod.pdf), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_quiz → F_html (T→F, mirror = htmlQuiz)
const quizHtml = PackagingMod.mkPackaging(IdMod.mk("P_quiz_html"), mkBuiltInRef("P_quiz_html"), Endpoint.mkTemplateEndpoint(Template.quiz.ref), Endpoint.mkFormatEndpoint(FormatMod.html), Fidelity.emptyFidelity, Maybe.just(Template.htmlQuiz.ref), builtInTrust, builtInFlavour);
// T_quiz → F_markdown (T→F, mirror = markdownQuiz)
const quizMarkdown = PackagingMod.mkPackaging(IdMod.mk("P_quiz_markdown"), mkBuiltInRef("P_quiz_markdown"), Endpoint.mkTemplateEndpoint(Template.quiz.ref), Endpoint.mkFormatEndpoint(FormatMod.markdown), Fidelity.emptyFidelity, Maybe.just(Template.markdownQuiz.ref), builtInTrust, builtInFlavour);
// T_question → F_html (T→F, mirror = htmlQuestion)
const questionHtml = PackagingMod.mkPackaging(IdMod.mk("P_question_html"), mkBuiltInRef("P_question_html"), Endpoint.mkTemplateEndpoint(Template.question.ref), Endpoint.mkFormatEndpoint(FormatMod.html), Fidelity.emptyFidelity, Maybe.just(Template.htmlQuestion.ref), builtInTrust, builtInFlavour);
// T_question → F_markdown (T→F, mirror = markdownQuestion)
const questionMarkdown = PackagingMod.mkPackaging(IdMod.mk("P_question_markdown"), mkBuiltInRef("P_question_markdown"), Endpoint.mkTemplateEndpoint(Template.question.ref), Endpoint.mkFormatEndpoint(FormatMod.markdown), Fidelity.emptyFidelity, Maybe.just(Template.markdownQuestion.ref), builtInTrust, builtInFlavour);
// T_pqbl_torus → F_torus (T→F, no mirror — the pqbl-torus template
// IS the target-shape carrier; the shell's renderer maps its
// Content directly to oli-torus MCQ activities via the pqbl-torus
// renderer in packages/server/src/infrastructure/renderer.ts).
const pqblTorusZip = PackagingMod.mkPackaging(IdMod.mk("P_pqbl_torus_torus"), mkBuiltInRef("P_pqbl_torus_torus"), Endpoint.mkTemplateEndpoint(Template.pqblTorus.ref), Endpoint.mkFormatEndpoint(FormatMod.torus), Fidelity.emptyFidelity, Maybe.nothing, builtInTrust, builtInFlavour);
// T_pqbl_torus → F_canvas (T→F, no mirror — the pqbl-torus template
// IS the source-shape carrier; the shell's canvas codec maps its
// Content to a Common Cartridge with QTI 1.2 Classic-Quiz assessments.
// Non-empty fidelity: the objectives / skills alignment graph has no
// Common Cartridge home and is dropped.)
const pqblTorusCanvas = PackagingMod.mkPackaging(IdMod.mk("P_pqbl_torus_canvas"), mkBuiltInRef("P_pqbl_torus_canvas"), Endpoint.mkTemplateEndpoint(Template.pqblTorus.ref), Endpoint.mkFormatEndpoint(FormatMod.canvas), Fidelity.mkFidelityContract(new Set(["lost_objective_alignment"])), Maybe.nothing, builtInTrust, builtInFlavour);
const builtInRegistry = new Map([
    [pdfImport.id, pdfImport],
    [freeformText.id, freeformText],
    [freeformTorus.id, freeformTorus],
    [freeformHtml.id, freeformHtml],
    [freeformPdf.id, freeformPdf],
    [pqblTorusZip.id, pqblTorusZip],
    [pqblTorusCanvas.id, pqblTorusCanvas],
    [pqblTorusHtml.id, pqblTorusHtml],
    [pqblTorusPdf.id, pqblTorusPdf],
    [quizHtml.id, quizHtml],
    [quizMarkdown.id, quizMarkdown],
    [questionHtml.id, questionHtml],
    [questionMarkdown.id, questionMarkdown],
]);
// === initial ===
//
// Registry pre-populated per the layer-defaults-are-runtime-invariants
// rule (reads never have to check for absence of the built-ins).
// Invocations + catalogue empty at boot.
const initial = {
    packagings: builtInRegistry,
    invocations: new Map(),
    catalogueEntries: new Map(),
};
// === Endpoint match helpers ===
//
// Refs are shallow `{ type, id, version }` records with primitive `id`
// (branded string) and `version` (branded number). Structural equality
// reduces to primitive `===` on those two fields — no deep walk needed.
const templateRefEq = (a, b) => a.id === b.id && a.version === b.version;
const formatRefEq = (a, b) => a.id === b.id && a.version === b.version;
// Does the endpoint match the given TemplateRef? A `format_endpoint`
// can never match a Template query — return `false` on that variant
// rather than smuggle it through.
const endpointMatchesTemplate = (endpoint, templateRef) => {
    switch (endpoint.type) {
        case "template_endpoint":
            return templateRefEq(endpoint.templateRef, templateRef);
        case "format_endpoint":
            return false;
        default:
            return exhaustSwitchCase(endpoint);
    }
};
const endpointMatchesFormat = (endpoint, formatRef) => {
    switch (endpoint.type) {
        case "template_endpoint":
            return false;
        case "format_endpoint":
            return formatRefEq(endpoint.formatRef, formatRef);
        default:
            return exhaustSwitchCase(endpoint);
    }
};
// === findByEndpoints ===
//
// Pure query over the registry — "which Packaging translates
// `sourceTemplate` into `targetFormat`?". Returns the first match;
// `None` if no entry has both endpoints aligned.
//
// This is a T→F query by construction (the signature takes a
// TemplateRef for source and a FormatRef for target). F→T entries in
// the registry (e.g. `P_pdf_import`) have inverted endpoints —
// `source: format_endpoint`, `target: template_endpoint` — and the
// endpoint match helpers cleanly reject them.
//
// If two entries ever match the same (source, target) pair we return
// the first. The grundversion has no such collision; post-MVP may
// want richer selection (tie-break by trust, flavour, or Author
// preference).
const findByEndpoints = (state, sourceTemplate, targetFormat) => {
    for (const packaging of state.packagings.values()) {
        if (endpointMatchesTemplate(packaging.source, sourceTemplate) &&
            endpointMatchesFormat(packaging.target, targetFormat)) {
            return Maybe.just(packaging);
        }
    }
    return Maybe.nothing;
};
// === packageableFormats ===
//
// "Which target Formats can a material of `sourceTemplate` be packaged
// into AND actually rendered end-to-end?" Two gates, both required:
//   1. a T→F Packaging edge exists in the registry from this template
//      (a codec maps the template's shape onto the format), AND
//   2. the Format is in `Format.catalogue` — the set whose shell
//      renderer is actually wired (`F_markdown` is deliberately kept
//      OUT of the catalogue until its renderer lands).
// A format that has a registry edge but no renderer (gate 1 without
// gate 2) is excluded, so a caller (the export-format dropdown) never
// offers a target that dead-ends in a `failed` export. Returns the
// matching FormatRefs in registry order, de-duplicated by id.
const packageableFormats = (state, sourceTemplate) => {
    const rendered = new Set(FormatMod.catalogue.map((entry) => entry.id));
    const seen = new Set();
    const formats = [];
    for (const packaging of state.packagings.values()) {
        const target = packaging.target;
        if (target.type !== "format_endpoint" ||
            !endpointMatchesTemplate(packaging.source, sourceTemplate)) {
            continue;
        }
        const id = FormatId.from(target.formatRef.id);
        if (!rendered.has(id) || seen.has(id))
            continue;
        seen.add(id);
        formats.push(target.formatRef);
    }
    return formats;
};
// === outputTemplateFor ===
//
// Pure rule about the `templateRef` a packaged Resource carries.
// Every T→F Packaging in the registry names a mirror Template whose
// Shape the output honours — return it when present. The one MVP
// exception is `P_freeform_text` (passthrough with `mirrorTemplateRef
// === none`); for it and any future passthrough entry the output
// preserves the source Template's shape.
//
// Encodes the domain rule "the packaged Resource inherits the mirror
// Template's shape when the packaging provides one; otherwise it
// preserves the source Template's shape".
const outputTemplateFor = (packaging, sourceTemplateRef) => Maybe.getOrElse(sourceTemplateRef)(packaging.mirrorTemplateRef);
//# sourceMappingURL=state.js.map